import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { GripHorizontal, X } from 'lucide-react';
import { CommentsPanel } from './CommentsPanel';
const STORAGE_KEY = 'landscope_comments_modal';
const MIN_WIDTH = 320;
const MIN_HEIGHT = 400;
const MAX_WIDTH = 800;
export const CommentsModal = ({ isOpen, onClose, projectId, scopeId, currentUserId, isProjectOwner = false, availableArtifacts = [], unsavedArtifacts = [], availableScopes = [], getArtifactInfo, onFlyToArtifact, onApplyScope, highlightCommentId, onHighlightCleared, preSelectedArtifactIds, unsavedPreSelectedArtifact, isPreSelectedArtifactDraft }) => {
    const windowRef = useRef(null);
    const [position, setPosition] = useState({ x: 100, y: 100 });
    const [size, setSize] = useState({ width: 400, height: 500 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [isResizing, setIsResizing] = useState(false);
    const [resizeEdge, setResizeEdge] = useState(null);
    const resizeStartRef = useRef({ x: 0, y: 0, width: 0, height: 0, posX: 0, posY: 0 });
    useEffect(() => {
        if (isOpen) {
            const maxX = Math.max(0, window.innerWidth - size.width);
            const maxY = Math.max(0, window.innerHeight - size.height - 20);
            const newX = Math.max(0, Math.min(maxX, position.x));
            const newY = Math.max(0, Math.min(maxY, position.y));
            if (newX !== position.x || newY !== position.y) {
                setPosition({ x: newX, y: newY });
            }
        }
    }, [isOpen]);
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        if (isOpen) {
            window.addEventListener('keydown', handleKeyDown);
        }
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);
    useEffect(() => {
        if (!isOpen)
            return;
        const timeout = setTimeout(() => {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ position, size }));
        }, 300);
        return () => clearTimeout(timeout);
    }, [position, size, isOpen]);
    const handleDragMouseDown = useCallback((e) => {
        if (windowRef.current) {
            const rect = windowRef.current.getBoundingClientRect();
            setDragOffset({
                x: e.clientX - rect.left,
                y: e.clientY - rect.top
            });
            setIsDragging(true);
        }
    }, []);
    const handleDragMouseMove = useCallback((e) => {
        if (isDragging) {
            const newX = Math.max(0, Math.min(window.innerWidth - size.width, e.clientX - dragOffset.x));
            const newY = Math.max(0, Math.min(window.innerHeight - 100, e.clientY - dragOffset.y));
            setPosition({ x: newX, y: newY });
        }
    }, [isDragging, dragOffset, size.width]);
    const handleDragMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);
    const handleResizeMouseDown = useCallback((e, edge) => {
        e.preventDefault();
        e.stopPropagation();
        setIsResizing(true);
        setResizeEdge(edge);
        resizeStartRef.current = {
            x: e.clientX,
            y: e.clientY,
            width: size.width,
            height: size.height,
            posX: position.x,
            posY: position.y
        };
    }, [size, position]);
    const handleResizeMouseMove = useCallback((e) => {
        if (!isResizing || !resizeEdge)
            return;
        const start = resizeStartRef.current;
        const deltaX = e.clientX - start.x;
        const deltaY = e.clientY - start.y;
        let newWidth = start.width;
        let newHeight = start.height;
        let newX = start.posX;
        let newY = start.posY;
        if (resizeEdge.includes('right')) {
            newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, start.width + deltaX));
        }
        if (resizeEdge.includes('bottom')) {
            const maxHeight = window.innerHeight - position.y - 20;
            newHeight = Math.max(MIN_HEIGHT, Math.min(maxHeight, start.height + deltaY));
        }
        if (resizeEdge.includes('left')) {
            const potentialWidth = start.width - deltaX;
            if (potentialWidth >= MIN_WIDTH && potentialWidth <= MAX_WIDTH) {
                newWidth = potentialWidth;
                newX = start.posX + deltaX;
            }
        }
        if (resizeEdge.includes('top')) {
            const potentialHeight = start.height - deltaY;
            if (potentialHeight >= MIN_HEIGHT) {
                newHeight = potentialHeight;
                newY = start.posY + deltaY;
            }
        }
        setSize({ width: newWidth, height: newHeight });
        setPosition({ x: newX, y: newY });
    }, [isResizing, resizeEdge, position.y]);
    const handleResizeMouseUp = useCallback(() => {
        setIsResizing(false);
        setResizeEdge(null);
    }, []);
    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', handleDragMouseMove);
            window.addEventListener('mouseup', handleDragMouseUp);
            return () => {
                window.removeEventListener('mousemove', handleDragMouseMove);
                window.removeEventListener('mouseup', handleDragMouseUp);
            };
        }
    }, [isDragging, handleDragMouseMove, handleDragMouseUp]);
    useEffect(() => {
        if (isResizing) {
            window.addEventListener('mousemove', handleResizeMouseMove);
            window.addEventListener('mouseup', handleResizeMouseUp);
            return () => {
                window.removeEventListener('mousemove', handleResizeMouseMove);
                window.removeEventListener('mouseup', handleResizeMouseUp);
            };
        }
    }, [isResizing, handleResizeMouseMove, handleResizeMouseUp]);
    if (!isOpen)
        return null;
    const title = 'Project Comments';
    return createPortal(_jsx(_Fragment, { children: _jsxs("div", { ref: windowRef, className: "fixed z-[70] rounded-lg shadow-2xl overflow-hidden flex flex-col", style: {
                left: `${position.x}px`,
                top: `${position.y}px`,
                width: `${size.width}px`,
                height: `${size.height}px`,
                backgroundColor: 'var(--charcoal)',
                border: '1px solid var(--pewter)',
                userSelect: isDragging || isResizing ? 'none' : 'auto',
            }, children: [_jsxs("div", { className: "flex items-center gap-2 px-3 py-2 cursor-move flex-shrink-0", style: { backgroundColor: 'var(--pewter)' }, onMouseDown: handleDragMouseDown, children: [_jsx(GripHorizontal, { size: 14, style: { color: 'var(--dew)', opacity: 0.5 } }), _jsx("span", { className: "text-sm font-medium flex-1 truncate", style: { color: 'var(--sand)' }, children: title }), _jsx("button", { onClick: (e) => {
                                e.stopPropagation();
                                onClose();
                            }, className: "p-1 rounded hover:bg-white/10 transition-colors", style: { color: 'var(--dew)' }, title: "Close", children: _jsx(X, { size: 16 }) })] }), _jsx("div", { className: "flex-1 min-h-0 overflow-hidden", children: _jsx(CommentsPanel, { projectId: projectId, scopeId: scopeId, currentUserId: currentUserId, isProjectOwner: isProjectOwner, onClose: onClose, availableArtifacts: availableArtifacts, unsavedArtifacts: unsavedArtifacts, availableScopes: availableScopes, getArtifactInfo: getArtifactInfo, onFlyToArtifact: onFlyToArtifact, onApplyScope: onApplyScope, highlightCommentId: highlightCommentId, onHighlightCleared: onHighlightCleared, preSelectedArtifactIds: preSelectedArtifactIds, unsavedPreSelectedArtifact: unsavedPreSelectedArtifact, isPreSelectedArtifactDraft: isPreSelectedArtifactDraft }) }), _jsx("div", { className: "absolute top-0 right-0 w-1 h-full cursor-ew-resize hover:bg-fern/30", onMouseDown: (e) => handleResizeMouseDown(e, 'right') }), _jsx("div", { className: "absolute bottom-0 left-0 w-full h-1 cursor-ns-resize hover:bg-fern/30", onMouseDown: (e) => handleResizeMouseDown(e, 'bottom') }), _jsx("div", { className: "absolute bottom-0 right-0 w-3 h-3 cursor-nwse-resize hover:bg-fern/30", onMouseDown: (e) => handleResizeMouseDown(e, 'bottom-right') }), _jsx("div", { className: "absolute top-0 left-0 w-1 h-full cursor-ew-resize hover:bg-fern/30", onMouseDown: (e) => handleResizeMouseDown(e, 'left') }), _jsx("div", { className: "absolute top-0 left-0 w-full h-1 cursor-ns-resize hover:bg-fern/30", onMouseDown: (e) => handleResizeMouseDown(e, 'top') }), _jsx("div", { className: "absolute top-0 left-0 w-3 h-3 cursor-nwse-resize hover:bg-fern/30", onMouseDown: (e) => handleResizeMouseDown(e, 'top-left') }), _jsx("div", { className: "absolute top-0 right-0 w-3 h-3 cursor-nesw-resize hover:bg-fern/30", onMouseDown: (e) => handleResizeMouseDown(e, 'top-right') }), _jsx("div", { className: "absolute bottom-0 left-0 w-3 h-3 cursor-nesw-resize hover:bg-fern/30", onMouseDown: (e) => handleResizeMouseDown(e, 'bottom-left') })] }) }), document.body);
};
